import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { WorkspaceBody, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Info, X } from 'lucide-react';

import { KeyInfoDarkImage, KeyInfoImage } from '@codes/assets';
import { cloudsKeys, useClouds, useRegions } from '@codes/cloud';
import { useCards } from '@codes/payments';
import { PrivacyTermsPage } from '@codes/shared';
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
    Input,
    Label,
    PageDialog,
    PageDialogContent,
    PageDialogFooter,
    PageDialogHeader,
    PageDialogTitle,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTrigger,
    Skeleton,
} from '@codes/ui-kit';
import { useCreateWorkspace } from '@codes/workspace';

import { AccessKeyDialog, CloudDetail, CloudList, EmailValidationDialog } from './components';
import { useDialogStore } from '../../stores';

interface CreateWorkspaceDialogProps {
    handleClose: () => void;
    handleComplete: (workspace: WorkspaceView) => void;
}

export const CreateWorkspaceDialogV2 = ({ handleClose, handleComplete }: CreateWorkspaceDialogProps) => {
    const { t } = useTranslation();

    const {
        data: clouds,
        isFetching: isFetchingClouds,
        isRefetching: isRefetchingClouds,
        error: cloudsFetchingError,
        refetch: refetchClouds,
    } = useClouds({});

    const [activeCloudId, setActiveCloudId] = useState<string | null>(null);
    const [termsAgreed, setTermsAgreed] = useState(false);

    const currentCloud = useMemo(() => {
        if (!clouds?.list) {
            return null;
        }
        if (!activeCloudId) {
            return clouds?.list.find(cloud => cloud.planType === 'basic' || cloud.planType === 'free') || null;
        }
        return clouds.list.find(cloud => cloud.id === activeCloudId);
    }, [clouds, activeCloudId]);

    const isSupportedCloud = useMemo(() => {
        if (!currentCloud) {
            return true;
        }
        return currentCloud.supported;
    }, [currentCloud]);

    const {
        data: regions,
        isFetching: isFetchingRegions,
        isRefetching: isRefetchingRegions,
        error: regionsFetchingError,
        refetch: refetchRegions,
    } = useRegions({ cloudId: currentCloud?.id });

    const { mutateAsync: createWorkspace, isPending: isCreatingWorkspace } = useCreateWorkspace();

    const {
        data: cards,
        isFetching: isFetchingCards,
        isRefetching: isRefetchingCards,
        error: cardsFetchingError,
        refetch: refetchCards,
    } = useCards({ stereo: 'stripe', view: 'mine' });

    const workspaceForm = useForm<WorkspaceBody>({
        defaultValues: {
            name: '',
            cloudId: '',
            regionId: '',
            request$: {
                email: '',
            },
            secretId: '',
        },
    });

    const { isWorkspaceDialogOpen, setWorkspaceDialog, setCardDialog } = useDialogStore(state => state);

    const queryClient = useQueryClient();

    const onSubmit = async (data: WorkspaceBody) => {
        if (!currentCloud) {
            return;
        }

        let body: WorkspaceBody = {};

        switch (currentCloud.planType) {
            case 'free':
            case 'basic':
                body = {
                    name: data.name?.trim(),
                    cloudId: currentCloud.id,
                    stereo: currentCloud.planType,
                };
                break;
            case 'pro':
                body = {
                    name: data.name?.trim(),
                    cloudId: currentCloud.id,
                    regionId: data.regionId,
                    request$: {
                        email: data.request$?.email as string,
                    },
                    stereo: currentCloud.planType,
                };
                break;
            case 'adv':
            case 'ent':
                body = {
                    name: data.name?.trim(),
                    cloudId: currentCloud.id,
                    regionId: data.regionId,
                    request$: {
                        email: data.request$?.email as string,
                    },
                    secretId: data.secretId,
                    stereo: currentCloud.planType,
                };
        }

        const workspace = await createWorkspace(body);
        handleComplete(workspace);
    };

    const onEmailSubmit = (email: string) => {
        workspaceForm.setValue('request$.email', email);
        workspaceForm.trigger('request$.email');
    };

    const onSecretSubmit = (secretId: string) => {
        workspaceForm.setValue('secretId', secretId);
        workspaceForm.trigger('secretId');

        // TODO: API 호출 로직 구현
    };

    const hasValidCards = !!cards?.list && cards.list.length > 0;

    const AddCardScreen = () => {
        if (!currentCloud) {
            return null;
        }

        if (currentCloud.planType === 'free' || currentCloud.planType === 'basic') {
            return (
                <div className="text-[20px] mb-8 font-medium text-gray-black">
                    {t('workspace.createV2.description')}
                </div>
            );
        }

        const isLoading = isFetchingCards || isRefetchingCards;

        if (isLoading) {
            return <Skeleton className="w-full h-44 rounded-lg  mb-8" />;
        }

        if (cardsFetchingError) {
            return (
                <div className="flex flex-col gap-y-2 items-center justify-center mb-8 h-44 bg-accent rounded-lg">
                    <div className="text-error  ">{t('workspace.createV2.errorLoadingCards')}</div>
                    <Button onClick={() => refetchCards()} className="">
                        {t('workspace.createV2.retryButton')}
                    </Button>
                </div>
            );
        }

        if (hasValidCards) {
            return (
                <div className="text-[20px] mb-8 font-medium text-gray-black">
                    {t('workspace.createV2.description')}
                </div>
            );
        }
        return (
            <Card className="relative h-[203px] px-[17px] py-5 overflow-hidden after:absolute after:inset-0 after:bg-white/55 after:content-[''] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.12)] mb-8">
                <div
                    className="absolute top-[-100px] left-[-50px] w-[280px] h-[280px] rounded-full pointer-events-none"
                    style={{
                        background:
                            'linear-gradient(83deg, rgba(255, 160, 101, 0.60) 5.08%, rgba(255, 193, 101, 0.60) 47.89%, rgba(255, 101, 193, 0.60) 94.13%)',
                        transform: 'rotate(-17.538deg)',
                        filter: 'blur(30px)',
                        opacity: 0.38,
                    }}
                />
                <div
                    className="absolute bottom-[-150px] right-[-100px] w-[352px] h-[352px] rounded-full pointer-events-none"
                    style={{
                        background:
                            'linear-gradient(76deg, rgba(255, 160, 101, 0.60) 11.71%, rgba(255, 193, 101, 0.60) 52.43%, rgba(255, 101, 193, 0.60) 96.41%)',
                        transform: 'rotate(-8.437deg)',
                        filter: 'blur(30px)',
                        opacity: 0.38,
                    }}
                />
                <div className="relative z-10 flex flex-col h-full items-start">
                    <div className="flex-1">
                        <div className="text-[20px] font-medium" style={{ lineHeight: 1.2 }}>
                            {t('workspace.createV2.addCardTitle')}
                        </div>
                        <div className="text-gray-600 mt-[7px] font-medium" style={{ lineHeight: 1.2 }}>
                            {t('workspace.createV2.addCardDesc')}
                        </div>
                    </div>
                    <Button className="mt-auto h-9 rounded-[10px] pl-[22px] pr-4" onClick={() => setCardDialog(true)}>
                        {t('workspace.createV2.addCardButton')} <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </Card>
        );
    };

    const isUsable = useMemo(() => {
        if (!currentCloud) {
            return false;
        }

        if (currentCloud.planType === 'free' || currentCloud.planType === 'basic') {
            return true;
        }

        return hasValidCards;
    }, [currentCloud, hasValidCards]);

    useEffect(() => {
        const resetData = async () => {
            await queryClient.resetQueries({ queryKey: cloudsKeys.list({}) });
            workspaceForm.reset();
        };

        if (isWorkspaceDialogOpen) {
            resetData();
        }
    }, [isWorkspaceDialogOpen]);

    useEffect(() => {
        workspaceForm.reset();
        setTermsAgreed(false);
    }, [activeCloudId]);

    return (
        <PageDialog open={isWorkspaceDialogOpen} onOpenChange={setWorkspaceDialog}>
            <PageDialogContent onInteractOutside={e => e.preventDefault()} onEscapeKeyDown={e => e.preventDefault()}>
                <div className="w-full max-w-[500px] sm:pt-[56px] sm:pb-[34px]">
                    <PageDialogHeader>
                        <PageDialogTitle>{t('workspace.createV2.title')}</PageDialogTitle>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="absolute left-6 top-[19px] z-[51]" type="button">
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogTitle>
                                    {t('workspace.create.confirmStopWorkspaceCreation')}
                                </AlertDialogTitle>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>{t('project.create.goBack')}</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleClose}>
                                        {t('project.create.stop')}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </PageDialogHeader>

                    <div className="pt-5 pb-10 px-6 overflow-auto">
                        <AddCardScreen />

                        <Label variant="form" className="flex items-center justify-between">
                            {t('workspace.createV2.cloudLabel')}
                            <a
                                href="https://eureka.codes/price"
                                target="_blank"
                                className="text-[#357AFF] font-medium text-[13px]"
                                rel="noreferrer"
                            >
                                {t('workspace.createV2.planDetailsButton')}
                            </a>
                        </Label>
                        <CloudList
                            isFetchingClouds={isFetchingClouds}
                            isRefetchingClouds={isRefetchingClouds}
                            cloudsFetchingError={cloudsFetchingError}
                            clouds={clouds}
                            currentCloud={currentCloud}
                            setActiveCloudId={setActiveCloudId}
                            refetchClouds={refetchClouds}
                        />
                        <CloudDetail currentCloud={currentCloud} />

                        {/* form */}
                        <form id="workspace-form" onSubmit={workspaceForm.handleSubmit(onSubmit)} className="mt-[22px]">
                            <div className="space-y-[22px]">
                                <div>
                                    <Label variant="form">{t('workspace.createV2.workspaceNameLabel')}</Label>
                                    <Controller
                                        name="name"
                                        control={workspaceForm.control}
                                        rules={{
                                            required: t('workspace.createV2.workspaceNameRequired'),
                                            validate: {
                                                notEmpty: value => {
                                                    const trimmed = value?.trim() || '';
                                                    if (trimmed.length === 0) {
                                                        return t('workspace.createV2.workspaceNameRequired');
                                                    }
                                                    if (trimmed.length < 3) {
                                                        return t('workspace.createV2.workspaceNameMinLength');
                                                    }
                                                    return true;
                                                },
                                            },
                                        }}
                                        render={({ field, fieldState }) => (
                                            <div>
                                                <Input
                                                    {...field}
                                                    placeholder={t('workspace.createV2.workspaceNamePlaceholder')}
                                                    disabled={!isUsable || isCreatingWorkspace || !isSupportedCloud}
                                                />
                                                {fieldState.error && (
                                                    <div className="text-error text-[13px] font-medium pl-[6px] mt-1">
                                                        {fieldState.error.message}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                                {(currentCloud?.planType === 'pro' ||
                                    currentCloud?.planType === 'adv' ||
                                    currentCloud?.planType === 'ent') && (
                                    <>
                                        <div>
                                            <Label variant="form">{t('workspace.createV2.emailLabel')}</Label>

                                            <Controller
                                                name="request$.email"
                                                control={workspaceForm.control}
                                                rules={{
                                                    required: t('workspace.createV2.emailRequired'),
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <div>
                                                        <EmailValidationDialog
                                                            field={field}
                                                            onEmailSubmit={onEmailSubmit}
                                                            disabled={
                                                                !isUsable || isCreatingWorkspace || !isSupportedCloud
                                                            }
                                                        />
                                                        {fieldState.error && (
                                                            <div className="text-error text-[13px] font-medium pl-[6px] mt-1">
                                                                {fieldState.error.message}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div>
                                            <Label variant="form">{t('workspace.createV2.regionLabel')}</Label>
                                            <Controller
                                                name="regionId"
                                                control={workspaceForm.control}
                                                rules={{
                                                    required: t('workspace.createV2.regionRequired'),
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <div className="w-full">
                                                        <Select
                                                            onValueChange={field.onChange}
                                                            value={field.value}
                                                            disabled={
                                                                !isUsable || isCreatingWorkspace || !isSupportedCloud
                                                            }
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue
                                                                    placeholder={t(
                                                                        'workspace.createV2.regionPlaceholder'
                                                                    )}
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {regions?.list.map(region => (
                                                                    <SelectItem
                                                                        value={region.id as string}
                                                                        key={region.id}
                                                                    >
                                                                        {region.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        {fieldState.error && (
                                                            <p className="mt-1 ml-[6px] text-xs text-error font-medium">
                                                                {fieldState.error.message}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                                {currentCloud?.planType === 'adv' ||
                                    (currentCloud?.planType === 'ent' && (
                                        <div>
                                            <Label variant="form" className="flex gap-x-2">
                                                {t('workspace.createV2.accessKeyLabel')}
                                                <Sheet>
                                                    <SheetTrigger asChild>
                                                        <Info className="w-[14px] h-[14px] text-placeholder cursor-pointer" />
                                                    </SheetTrigger>
                                                    <SheetContent
                                                        className="overflow-hidden"
                                                        aria-describedby={undefined}
                                                    >
                                                        <SheetHeader className="justify-end">
                                                            <SheetClose>
                                                                <X className="w-5 h-5" />
                                                            </SheetClose>
                                                        </SheetHeader>

                                                        <Image
                                                            className="w-full mt-5"
                                                            src={KeyInfoImage}
                                                            darkSrc={KeyInfoDarkImage}
                                                        />
                                                    </SheetContent>
                                                </Sheet>
                                            </Label>
                                            <Controller
                                                name="secretId"
                                                control={workspaceForm.control}
                                                rules={{
                                                    required: t('workspace.createV2.accessKeyRequired'),
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <div>
                                                        <AccessKeyDialog
                                                            field={field}
                                                            onSecretSubmit={onSecretSubmit}
                                                            disabled={
                                                                !isUsable || isCreatingWorkspace || !isSupportedCloud
                                                            }
                                                        />
                                                        {fieldState.error && (
                                                            <div className="text-error text-[13px] font-medium pl-[6px] mt-1">
                                                                {fieldState.error.message}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </form>

                        {/* 약관동의 */}
                        <div className="flex items-center justify-between mt-[30px]">
                            <button
                                type="button"
                                className="flex items-center gap-[6px]"
                                onClick={() => setTermsAgreed(!termsAgreed)}
                                disabled={!isUsable || isCreatingWorkspace || !isSupportedCloud}
                            >
                                <div
                                    className={`w-4 h-4 border-[1.5px] rounded-full flex items-center justify-center ${
                                        termsAgreed ? 'border-primary' : 'border-gray-300'
                                    }`}
                                >
                                    {termsAgreed && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                </div>
                                <div className="text-sm text-gray-600 font-medium cursor-pointer">
                                    {t('workspace.createV2.termsAgreement')}
                                </div>
                            </button>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <button
                                        className="text-sm text-gray-600 underline hover:text-gray-900"
                                        disabled={!isUsable || isCreatingWorkspace || !isSupportedCloud}
                                    >
                                        {t('workspace.createV2.termsView')}
                                    </button>
                                </DialogTrigger>
                                <DialogPortal>
                                    <DialogContent className="max-md:max-w-full max-md:w-full max-md:max-h-[100vh] max-w-[800px] max-md:rounded-none p-0">
                                        <DialogHeader>
                                            <DialogTitle>Privacy & Terms</DialogTitle>
                                        </DialogHeader>
                                        <PrivacyTermsPage isDialog={true} />
                                    </DialogContent>
                                </DialogPortal>
                            </Dialog>
                        </div>
                    </div>
                    <PageDialogFooter>
                        <Button
                            type="submit"
                            size="lg"
                            className="w-full"
                            form="workspace-form"
                            disabled={!termsAgreed || isCreatingWorkspace || !isSupportedCloud}
                        >
                            {isCreatingWorkspace ? (
                                <SharedSpinner />
                            ) : isSupportedCloud ? (
                                t('workspace.createV2.createButton')
                            ) : (
                                t('workspace.createV2.unsupportedCloudWarning')
                            )}
                        </Button>
                    </PageDialogFooter>
                </div>
            </PageDialogContent>
        </PageDialog>
    );
};
